import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";

export default function PublicSurvey() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/public/survey/${token}`).then(({ data }) => setD(data)).catch(() => setD(false)); }, [token]);
  useEffect(() => { applyBrandColor(d?.shop?.brand_color || DEFAULT_BRAND_COLOR); return () => applyBrandColor(DEFAULT_BRAND_COLOR); }, [d]);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/public/survey/${token}`, { rating, comment }); setDone({ rating, google: data.google_url }); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (d === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  if (d === false) return <div className="min-h-screen flex items-center justify-center text-zinc-400 p-6">Link inválido.</div>;
  const final = done || (d.answered ? { rating: d.rating, google: d.google_url } : null);
  return (
    <div className="min-h-screen px-4 py-10">
      <div className="max-w-md mx-auto space-y-5 text-center">
        {d.shop.logo_path ? <img src={shopLogoUrl(d.shop.id)} alt={d.shop.name} className="mx-auto h-16" /> : null}
        <div className="font-display text-2xl font-black uppercase text-zinc-100">{d.shop.name}</div>
        <div className="rounded-2xl border border-card2-border bg-card2 p-6 space-y-4" data-testid="survey-box">
          {final ? (
            <div data-testid="survey-thanks" className="space-y-3">
              <div className="text-lg font-bold text-zinc-100">Obrigado pela avaliação!</div>
              {final.rating >= 4 && final.google
                ? <><p className="text-sm text-zinc-400">Que bom que gostou! Pode contar isso no Google? Ajuda muito a oficina.</p>
                  <a data-testid="survey-google" href={final.google} target="_blank" rel="noopener noreferrer" className="inline-flex h-12 px-6 items-center rounded-xl font-bold" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}>Avaliar no Google</a></>
                : <p className="text-sm text-zinc-400">{final.rating <= 3 ? "Sentimos muito. A oficina vai entrar em contato para resolver." : "Sua opinião ajuda a melhorar o atendimento."}</p>}
            </div>
          ) : (
            <>
              <div className="text-zinc-200">Como foi o serviço no veículo <b className="font-mono">{d.plate}</b>?</div>
              <div className="flex justify-center gap-2">{[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`survey-star-${n}`} onClick={() => setRating(n)} aria-label={`${n} estrelas`}>
                  <Star className={`h-10 w-10 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                </button>
              ))}</div>
              <Textarea data-testid="survey-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Quer comentar algo? (opcional)" className="bg-zinc-900 border-zinc-800 text-base" />
              <Button data-testid="survey-send" disabled={!rating || busy} onClick={send} className="w-full h-12 font-bold uppercase" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}>Enviar avaliação</Button>
            </>
          )}
        </div>
        <p className="text-[11px] font-mono-caps text-zinc-600 tracking-widest">feito com AutoOficina</p>
      </div>
    </div>
  );
}
